import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { LogOut, Plus, Trash2, Edit, X, Search, ChevronLeft, ChevronRight, Upload as UploadIcon, FileText, ArrowUp, ArrowDown, Copy, Download, Info, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import MediaUploader from '@/components/MediaUploader';
import NavigationMenu from '@/components/NavigationMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [rules, setRules] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [editingRule, setEditingRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(false);

  // Search, Filter & Pagination
  const [searchKeyword, setSearchKeyword] = useState('');
  const [filterType, setFilterType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRules, setTotalRules] = useState(0);
  const pageSize = 10;

  // Toggle between upload and URL input
  const [useUpload, setUseUpload] = useState({
    image: false,
    video: false,
    audio: false,
    document: false,
    cta_header: false,
    button_header: false
  });

  const [formData, setFormData] = useState({
    keyword: '',
    match_type: 'exact',
    case_sensitive: false,
    is_active: true,
    response_type: 'text',
    response_payload: { body: '' },
    mark_as_seen: false,
    add_reaction: '',
    typing_indicator: false,
    contextual_reply: false
  });

  // Track upload state
  const [isUploading, setIsUploading] = useState(false);
  const formRef = React.useRef(null);

  // Multiple responses mode
  const [multiResponseMode, setMultiResponseMode] = useState(false);
  const [responses, setResponses] = useState([
    { response_type: 'text', response_payload: { body: '' } }
  ]);

  useEffect(() => {
    // Check if admin is managing rules for a specific account
    const adminAccount = sessionStorage.getItem('adminManageAccount');
    if (adminAccount && user?.role === 'admin') {
      const account = JSON.parse(adminAccount);
      setIsAdminMode(true);
      setAccounts([account]);
      setSelectedAccount(account.id);
    } else {
      fetchAccounts();
    }
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchRules(selectedAccount, currentPage, searchKeyword, filterType);
    }
  }, [selectedAccount, currentPage, searchKeyword, filterType]);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/accounts`);
      setAccounts(response.data);
      if (response.data.length > 0 && !selectedAccount) {
        setSelectedAccount(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load accounts');
    }
  };

  const fetchRules = async (accountId, page = 1, search = '', type = '') => {
    try {
      let url = `${API}/rules?account_id=${accountId}&page=${page}&page_size=${pageSize}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (type) url += `&response_type=${type}`;
      
      const response = await axios.get(url);
      // Handle paginated response
      if (response.data.rules) {
        setRules(response.data.rules);
        setTotalPages(response.data.total_pages || 1);
        setTotalRules(response.data.total || 0);
      } else {
        setRules(response.data);
      }
    } catch (error) {
      toast.error('Failed to load rules');
    }
  };

  const resetForm = () => {
    setFormData({
      keyword: '',
      match_type: 'exact',
      case_sensitive: false,
      is_active: true,
      response_type: 'text',
      response_payload: { body: '' },
      mark_as_seen: false,
      add_reaction: '',
      typing_indicator: false,
      contextual_reply: false
    });
    setMultiResponseMode(false);
    setResponses([{ response_type: 'text', response_payload: { body: '' } }]);
    setEditingRule(null);
    setShowRuleForm(false);
    setUseUpload({ image: false, video: false, audio: false, document: false });
  };

  const handleSearch = (value) => {
    setSearchKeyword(value);
    setCurrentPage(1);
  };

  const handleFilterType = (value) => {
    setFilterType(value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Multiple responses handlers
  const addResponse = () => {
    if (responses.length < 5) {
      setResponses([...responses, { response_type: 'text', response_payload: { body: '' } }]);
    } else {
      toast.error('Maksimal 5 respons per keyword');
    }
  };

  const removeResponse = (index) => {
    if (responses.length > 1) {
      setResponses(responses.filter((_, i) => i !== index));
    } else {
      toast.error('Minimal 1 respons diperlukan');
    }
  };

  const updateResponse = (index, field, value) => {
    const newResponses = [...responses];
    if (field === 'response_type') {
      newResponses[index].response_type = value;
      // Reset payload when type changes with appropriate defaults
      if (value === 'text') {
        newResponses[index].response_payload = { body: '' };
      } else if (value === 'button') {
        newResponses[index].response_payload = { body: '', buttons: [{ id: '', title: '' }] };
      } else if (value === 'cta_url') {
        newResponses[index].response_payload = { body: '', url: '', button_text: '' };
      } else if (value === 'location') {
        newResponses[index].response_payload = { name: '', address: '', latitude: '', longitude: '' };
      } else if (value === 'contact') {
        newResponses[index].response_payload = { name: '', phone: '' };
      } else {
        newResponses[index].response_payload = {};
      }
    } else {
      newResponses[index].response_payload = value;
    }
    setResponses(newResponses);
  };

  const moveResponse = (index, direction) => {
    if (direction === 'up' && index > 0) {
      const newResponses = [...responses];
      [newResponses[index - 1], newResponses[index]] = [newResponses[index], newResponses[index - 1]];
      setResponses(newResponses);
    } else if (direction === 'down' && index < responses.length - 1) {
      const newResponses = [...responses];
      [newResponses[index], newResponses[index + 1]] = [newResponses[index + 1], newResponses[index]];
      setResponses(newResponses);
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule.id);
    
    // Check if it's multiple response format
    if (rule.responses && rule.responses.length > 0) {
      setMultiResponseMode(true);
      setResponses(rule.responses);
      setFormData({
        keyword: rule.keyword,
        match_type: rule.match_type,
        case_sensitive: rule.case_sensitive || false,
        is_active: rule.is_active !== false,
        response_type: '',
        response_payload: {},
        mark_as_seen: rule.mark_as_seen || false,
        add_reaction: rule.add_reaction || '',
        typing_indicator: rule.typing_indicator || false,
        contextual_reply: rule.contextual_reply || false
      });
    } else {
      // Single response format
      setMultiResponseMode(false);
      setFormData({
        keyword: rule.keyword,
        match_type: rule.match_type,
        case_sensitive: rule.case_sensitive || false,
        is_active: rule.is_active !== false,
        response_type: rule.response_type,
        response_payload: rule.response_payload,
        mark_as_seen: rule.mark_as_seen || false,
        add_reaction: rule.add_reaction || '',
        typing_indicator: rule.typing_indicator || false,
        contextual_reply: rule.contextual_reply || false
      });
    }
    
    setShowRuleForm(true);
    
    // Scroll to form smoothly after a short delay
    setTimeout(() => {
      formRef.current?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start'
      });
    }, 100);
  };

  const handleExportRules = async () => {
    if (!selectedAccount) {
      toast.error('Pilih akun terlebih dahulu');
      return;
    }
    
    try {
      const response = await axios.get(`${API}/rules/export/${selectedAccount}`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chatbot-rules-${selectedAccount}-${new Date().toISOString().split('T')[0]}.json`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success('Rules berhasil di-export!');
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Gagal export rules');
    }
  };

  const handleImportRules = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!selectedAccount) {
      toast.error('Pilih akun terlebih dahulu');
      return;
    }
    
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      const response = await axios.post(`${API}/rules/import/${selectedAccount}`, data);
      toast.success(`Berhasil import ${response.data.imported} rules`);
      fetchRules(selectedAccount, currentPage, searchKeyword, filterType);
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Gagal import rules');
    }
    
    // Reset input
    event.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validation for multi-response mode
      if (multiResponseMode) {
        // Check button responses have at least 1 button
        for (let i = 0; i < responses.length; i++) {
          const resp = responses[i];
          if (resp.response_type === 'button') {
            if (!resp.response_payload.buttons || resp.response_payload.buttons.length === 0) {
              toast.error(`Respons ${i + 1}: Tombol Balasan harus memiliki minimal 1 tombol`);
              setLoading(false);
              return;
            }
            // Check buttons have ID and title
            for (let j = 0; j < resp.response_payload.buttons.length; j++) {
              const btn = resp.response_payload.buttons[j];
              if (!btn.id || !btn.title) {
                toast.error(`Respons ${i + 1}, Tombol ${j + 1}: ID dan Title harus diisi`);
                setLoading(false);
                return;
              }
            }
          }
        }
      }

      let payload;
      
      if (multiResponseMode) {
        // Multiple responses format
        payload = {
          account_id: selectedAccount,
          keyword: formData.keyword,
          match_type: formData.match_type,
          case_sensitive: formData.case_sensitive,
          responses: responses,
          mark_as_seen: formData.mark_as_seen,
          add_reaction: formData.add_reaction
        };
      } else {
        // Single response format
        payload = {
          account_id: selectedAccount,
          ...formData
        };
      }

      if (editingRule) {
        await axios.put(`${API}/rules/${editingRule}`, multiResponseMode ? {
          keyword: formData.keyword,
          match_type: formData.match_type,
          case_sensitive: formData.case_sensitive,
          responses: responses,
          mark_as_seen: formData.mark_as_seen,
          add_reaction: formData.add_reaction
        } : formData);
        toast.success('Rule updated successfully!');
      } else {
        await axios.post(`${API}/rules`, payload);
        toast.success('Rule created successfully!');
      }

      resetForm();
      fetchRules(selectedAccount);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save rule');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ruleId) => {
    try {
      await axios.delete(`${API}/rules/${ruleId}`);
      toast.success('Rule deleted successfully!');
      fetchRules(selectedAccount);
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (ruleId, currentStatus) => {
    try {
      await axios.put(`${API}/rules/${ruleId}`, {
        is_active: !currentStatus
      });
      toast.success(`Rule ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
      fetchRules(selectedAccount);
    } catch (error) {
      toast.error('Failed to toggle rule status');
    }
  };

  const handleResponseTypeChange = (type) => {
    let newPayload = {};
    switch (type) {
      case 'text':
        newPayload = { body: '' };
        break;
      case 'image':
        newPayload = { link: '', caption: '' };
        break;
      case 'video':
        newPayload = { link: '' };
        break;
      case 'document':
        newPayload = { link: '', filename: '', caption: '' };
        break;
      case 'audio':
        newPayload = { link: '' };
        break;
      case 'location':
        newPayload = { latitude: '', longitude: '', name: '', address: '' };
        break;
      case 'contact':
        newPayload = { name: '', first_name: '', phone: '' };
        break;
      case 'button':
        newPayload = { body: '', buttons: [{ id: '', title: '' }], header_image: '' };
        break;
      case 'cta_url':
        newPayload = { body: '', button_text: 'Kunjungi', url: '', header_image: '' };
        break;
      case 'list':
        newPayload = {
          body: '',
          buttonText: 'Menu',
          sections: [{ title: '', rows: [{ id: '', title: '', description: '' }] }]
        };
        break;
      default:
        newPayload = { body: '' };
    }
    setFormData({ ...formData, response_type: type, response_payload: newPayload });
  };

  const addButton = () => {
    const buttons = [...formData.response_payload.buttons, { id: '', title: '' }];
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, buttons }
    });
  };

  const removeButton = (index) => {
    const buttons = formData.response_payload.buttons.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, buttons }
    });
  };

  const updateButton = (index, field, value) => {
    const buttons = [...formData.response_payload.buttons];
    buttons[index][field] = value;
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, buttons }
    });
  };

  const addSection = () => {
    const sections = [...formData.response_payload.sections, { title: '', rows: [{ id: '', title: '', description: '' }] }];
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, sections }
    });
  };

  const removeSection = (index) => {
    const sections = formData.response_payload.sections.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, sections }
    });
  };

  const updateSection = (index, field, value) => {
    const sections = [...formData.response_payload.sections];
    sections[index][field] = value;
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, sections }
    });
  };

  const addRow = (sectionIndex) => {
    const sections = [...formData.response_payload.sections];
    // Count total rows across all sections
    const totalRows = sections.reduce((sum, section) => sum + section.rows.length, 0);
    if (totalRows >= 10) {
      toast.error('Maksimal 10 baris untuk semua seksi');
      return;
    }
    sections[sectionIndex].rows.push({ id: '', title: '', description: '' });
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, sections }
    });
  };

  const removeRow = (sectionIndex, rowIndex) => {
    const sections = [...formData.response_payload.sections];
    sections[sectionIndex].rows = sections[sectionIndex].rows.filter((_, i) => i !== rowIndex);
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, sections }
    });
  };

  const updateRow = (sectionIndex, rowIndex, field, value) => {
    const sections = [...formData.response_payload.sections];
    sections[sectionIndex].rows[rowIndex][field] = value;
    setFormData({
      ...formData,
      response_payload: { ...formData.response_payload, sections }
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <NavigationMenu />
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-800" data-testid="user-dashboard-title">Kelola Chatbot</h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">Selamat datang, {user?.email}</p>
            </div>
          </div>
        </div>

        {accounts.length === 0 ? (
          <Card className="border-2 border-blue-200 shadow-lg">
            <CardContent className="text-center py-12">
              <p className="text-gray-600 text-lg" data-testid="no-accounts-assigned">Belum ada akun WhatsApp yang ditugaskan kepada Anda. Silakan hubungi administrator.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Account Selector */}
            <Card className="border-2 border-blue-200 shadow-lg">
              <CardHeader>
                <CardTitle>Pilih Akun WhatsApp</CardTitle>
                <CardDescription>Pilih akun yang ingin Anda kelola</CardDescription>
              </CardHeader>
              <CardContent>
                <Select value={selectedAccount} onValueChange={setSelectedAccount}>
                  <SelectTrigger data-testid="account-selector">
                    <SelectValue placeholder="Pilih akun" />
                  </SelectTrigger>
                  <SelectContent>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.name} - {account.phone_number_id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </CardContent>
            </Card>

            {selectedAccount && (
              <>
                {/* Rule Form */}
                {showRuleForm && (
                <Card ref={formRef} className="border-2 border-blue-200 shadow-lg transition-all duration-500 ease-in-out scale-in">
                  <CardHeader>
                    <div className="flex justify-between items-center">
                      <div>
                        <CardTitle>{editingRule ? 'Edit Aturan' : 'Buat Aturan Baru'}</CardTitle>
                        <CardDescription>Tentukan keyword pemicu dan respon</CardDescription>
                      </div>
                      <Button variant="ghost" size="sm" onClick={resetForm}>
                        <X className="w-5 h-5" />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4" data-testid="rule-form">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="keyword">Keyword Pemicu</Label>
                          <Input
                            id="keyword"
                            value={formData.keyword}
                            onChange={(e) => setFormData({ ...formData, keyword: e.target.value })}
                            placeholder="menu, halo, harga"
                            required
                            data-testid="keyword-input"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            💡 Tip: Pisahkan dengan koma untuk multiple keywords. Contoh: "halo, hai, hello, hi"
                          </p>
                          <div className="flex items-center space-x-2 mt-2">
                            <Switch
                              id="case-sensitive"
                              checked={formData.case_sensitive}
                              onCheckedChange={(checked) => setFormData({ ...formData, case_sensitive: checked })}
                              data-testid="case-sensitive-switch"
                            />
                            <Label htmlFor="case-sensitive" className="text-sm font-normal cursor-pointer">
                              Case Sensitive (MENU ≠ menu)
                            </Label>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="match_type">Tipe Pencocokan</Label>
                          <Select
                            value={formData.match_type}
                            onValueChange={(value) => setFormData({ ...formData, match_type: value })}
                          >
                            <SelectTrigger data-testid="match-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="exact">Cocok Persis</SelectItem>
                              <SelectItem value="contains">Mengandung</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      {/* Additional Options */}
                      <div className="border-t pt-4">
                        <Label className="text-base font-semibold mb-3 block">Opsi Tambahan</Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id="mark-as-seen"
                              checked={formData.mark_as_seen}
                              onCheckedChange={(checked) => setFormData({ ...formData, mark_as_seen: checked })}
                              data-testid="mark-as-seen-switch"
                            />
                            <Label htmlFor="mark-as-seen" className="text-sm font-normal cursor-pointer">
                              Mark as Seen (Centang Biru)
                            </Label>
                          </div>
                          
                          <TooltipProvider>
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="typing-indicator"
                                checked={formData.typing_indicator}
                                onCheckedChange={(checked) => setFormData({ ...formData, typing_indicator: checked })}
                                data-testid="typing-indicator-switch"
                              />
                              <Label htmlFor="typing-indicator" className="text-sm font-normal cursor-pointer">
                                Indikator Mengetik
                              </Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">Tampilkan status "mengetik..." sebelum mengirim balasan. Otomatis menandai pesan sebagai telah dibaca (centang biru).</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                          
                          <TooltipProvider>
                            <div className="flex items-center space-x-2">
                              <Switch
                                id="contextual-reply"
                                checked={formData.contextual_reply}
                                onCheckedChange={(checked) => setFormData({ ...formData, contextual_reply: checked })}
                                data-testid="contextual-reply-switch"
                              />
                              <Label htmlFor="contextual-reply" className="text-sm font-normal cursor-pointer">
                                Kutip Pesan
                              </Label>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <HelpCircle className="w-4 h-4 text-gray-400 cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p className="max-w-xs">Balas dengan mengutip pesan pengirim (quote reply)</p>
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </TooltipProvider>
                          
                          <div>
                            <Label htmlFor="add-reaction" className="text-sm">Reaksi Emoji (Opsional)</Label>
                            <Input
                              id="add-reaction"
                              value={formData.add_reaction}
                              onChange={(e) => setFormData({ ...formData, add_reaction: e.target.value })}
                              placeholder="👍 ❤️ 😊 🔥"
                              maxLength={2}
                              className="text-2xl"
                              data-testid="reaction-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Contoh: 👍, ❤️, 😊, 🔥</p>
                          </div>
                        </div>
                      </div>

                      {/* Multiple Response Toggle */}
                      <div className="border-t pt-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <Label className="text-base font-semibold">Mode Respons</Label>
                            <p className="text-xs text-gray-500 mt-1">
                              {multiResponseMode ? `${responses.length}/5 respons` : 'Satu respons per keyword'}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant={multiResponseMode ? "default" : "outline"}
                            size="sm"
                            onClick={() => {
                              setMultiResponseMode(!multiResponseMode);
                              if (!multiResponseMode) {
                                // Switching to multi mode - convert current single response
                                if (formData.response_type) {
                                  setResponses([{
                                    response_type: formData.response_type,
                                    response_payload: formData.response_payload
                                  }]);
                                }
                              }
                            }}
                            className="transition-all duration-200"
                          >
                            <Copy className="w-4 h-4 mr-2" />
                            {multiResponseMode ? 'Multi Respons' : 'Single Respons'}
                          </Button>
                        </div>
                      </div>

                      {!multiResponseMode && (
                        <>
                      {/* Two Column Layout for Response Type Selection */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left Column - Dropdown */}
                        <div>
                          <Label htmlFor="response_type" className="text-base font-semibold mb-3 block">Tipe Respons</Label>
                          <p className="text-sm text-gray-600 mb-3">Satu respons per keyword</p>
                          <Select
                            value={formData.response_type}
                            onValueChange={handleResponseTypeChange}
                          >
                            <SelectTrigger data-testid="response-type-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="text">Teks</SelectItem>
                              <SelectItem value="image">Gambar</SelectItem>
                              <SelectItem value="video">Video</SelectItem>
                              <SelectItem value="audio">Audio</SelectItem>
                              <SelectItem value="document">Dokumen</SelectItem>
                              <SelectItem value="location">Lokasi</SelectItem>
                              <SelectItem value="contact">Kontak</SelectItem>
                              <SelectItem value="button">Tombol Balasan</SelectItem>
                              <SelectItem value="cta_url">Tombol URL</SelectItem>
                              <SelectItem value="list">Daftar Interaktif</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Right Column - Example Image */}
                        <div>
                          <Label className="text-base font-semibold mb-3 block">Contoh Layout:</Label>
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4 min-h-[200px] flex items-center justify-center">
                            {formData.response_type === 'text' && (
                              <div className="text-center text-gray-500">
                                <FileText className="w-16 h-16 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">Pesan teks sederhana</p>
                              </div>
                            )}
                            {formData.response_type === 'image' && (
                              <img 
                                src="/examples/image.png" 
                                alt="Contoh gambar" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'video' && (
                              <img 
                                src="/examples/video.png" 
                                alt="Contoh video" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'audio' && (
                              <img 
                                src="/examples/audio.png" 
                                alt="Contoh audio" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'document' && (
                              <img 
                                src="/examples/document.png" 
                                alt="Contoh dokumen" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'location' && (
                              <img 
                                src="/examples/location.png" 
                                alt="Contoh lokasi" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'contact' && (
                              <img 
                                src="/examples/contact.png" 
                                alt="Contoh kontak" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'button' && (
                              <img 
                                src="/examples/button.png" 
                                alt="Contoh tombol balasan" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'cta_url' && (
                              <img 
                                src="/examples/cta_url.png" 
                                alt="Contoh tombol URL" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                            {formData.response_type === 'list' && (
                              <img 
                                src="/examples/list_message.png" 
                                alt="Contoh daftar interaktif" 
                                className="w-full h-auto rounded-lg shadow-lg"
                              />
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Conditional Response Fields */}
                      {formData.response_type === 'text' && (
                        <div>
                          <Label htmlFor="body">Teks Pesan</Label>
                          <Textarea
                            id="body"
                            value={formData.response_payload.body}
                            onChange={(e) => setFormData({
                              ...formData,
                              response_payload: { body: e.target.value }
                            })}
                            placeholder="Masukkan pesan Anda..."
                            rows={4}
                            required
                            data-testid="text-body-input"
                          />
                        </div>
                      )}

                      {(formData.response_type === 'image') && (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <Button
                              type="button"
                              variant={useUpload.image ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, image: true })}
                            >
                              <UploadIcon className="w-4 h-4 mr-1" />
                              Upload File
                            </Button>
                            <Button
                              type="button"
                              variant={!useUpload.image ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, image: false })}
                            >
                              Gunakan URL
                            </Button>
                          </div>
                          {useUpload.image ? (
                            <MediaUploader
                              mediaType="image"
                              accept="image/*"
                              onUploadStart={() => setIsUploading(true)}
                              onUploadEnd={() => setIsUploading(false)}
                              onUploadComplete={(url) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, link: url }
                              })}
                              label="Upload Gambar"
                            />
                          ) : (
                            <div>
                              <Label htmlFor="link">URL Gambar</Label>
                              <Input
                                id="link"
                                value={formData.response_payload.link}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, link: e.target.value }
                                })}
                                placeholder="https://example.com/image.jpg"
                                required
                                data-testid="media-link-input"
                              />
                            </div>
                          )}
                          <div>
                            <Label htmlFor="caption">Caption (Opsional)</Label>
                            <Textarea
                              id="caption"
                              value={formData.response_payload.caption || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, caption: e.target.value }
                              })}
                              placeholder="Tambahkan caption..."
                              rows={2}
                              data-testid="media-caption-input"
                            />
                          </div>
                        </>
                      )}

                      {formData.response_type === 'video' && (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <Button
                              type="button"
                              variant={useUpload.video ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, video: true })}
                            >
                              <UploadIcon className="w-4 h-4 mr-1" />
                              Upload File
                            </Button>
                            <Button
                              type="button"
                              variant={!useUpload.video ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, video: false })}
                            >
                              Gunakan URL
                            </Button>
                          </div>
                          {useUpload.video ? (
                            <MediaUploader
                              mediaType="video"
                              accept="video/mp4,video/3gpp"
                              onUploadStart={() => setIsUploading(true)}
                              onUploadEnd={() => setIsUploading(false)}
                              onUploadComplete={(url) => setFormData({
                                ...formData,
                                response_payload: { link: url }
                              })}
                              label="Upload Video"
                              showPreview={false}
                            />
                          ) : (
                            <div>
                              <Label htmlFor="video_link">URL Video</Label>
                              <Input
                                id="video_link"
                                value={formData.response_payload.link}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { link: e.target.value }
                                })}
                                placeholder="https://example.com/video.mp4"
                                required
                                data-testid="video-link-input"
                              />
                            </div>
                          )}
                          <div>
                            <Label htmlFor="video_caption">Caption (Opsional)</Label>
                            <Textarea
                              id="video_caption"
                              value={formData.response_payload.caption || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, caption: e.target.value }
                              })}
                              placeholder="Tambahkan caption..."
                              rows={2}
                              data-testid="video-caption-input"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Format: MP4/3GP, Maks 16MB</p>
                        </>
                      )}

                      {formData.response_type === 'document' && (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <Button
                              type="button"
                              variant={useUpload.document ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, document: true })}
                            >
                              <UploadIcon className="w-4 h-4 mr-1" />
                              Upload File
                            </Button>
                            <Button
                              type="button"
                              variant={!useUpload.document ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, document: false })}
                            >
                              Gunakan URL
                            </Button>
                          </div>
                          {useUpload.document ? (
                            <MediaUploader
                              mediaType="document"
                              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                              onUploadStart={() => setIsUploading(true)}
                              onUploadEnd={() => setIsUploading(false)}
                              onUploadComplete={(url) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, link: url }
                              })}
                              label="Upload Dokumen"
                              showPreview={false}
                            />
                          ) : (
                            <div>
                              <Label htmlFor="doc_link">URL Dokumen</Label>
                              <Input
                                id="doc_link"
                                value={formData.response_payload.link}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, link: e.target.value }
                                })}
                                placeholder="https://example.com/document.pdf"
                                required
                                data-testid="doc-link-input"
                              />
                            </div>
                          )}
                          <div>
                            <Label htmlFor="filename">Nama File</Label>
                            <Input
                              id="filename"
                              value={formData.response_payload.filename || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, filename: e.target.value }
                              })}
                              placeholder="dokumen.pdf"
                              data-testid="doc-filename-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="doc_caption">Caption (Opsional)</Label>
                            <Textarea
                              id="doc_caption"
                              value={formData.response_payload.caption || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, caption: e.target.value }
                              })}
                              placeholder="Tambahkan caption..."
                              rows={2}
                              data-testid="doc-caption-input"
                            />
                          </div>
                        </>
                      )}

                      {formData.response_type === 'audio' && (
                        <>
                          <div className="flex items-center gap-2 mb-2">
                            <Button
                              type="button"
                              variant={useUpload.audio ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, audio: true })}
                            >
                              <UploadIcon className="w-4 h-4 mr-1" />
                              Upload File
                            </Button>
                            <Button
                              type="button"
                              variant={!useUpload.audio ? "default" : "outline"}
                              size="sm"
                              onClick={() => setUseUpload({ ...useUpload, audio: false })}
                            >
                              Gunakan URL
                            </Button>
                          </div>
                          {useUpload.audio ? (
                            <MediaUploader
                              mediaType="audio"
                              accept="audio/*"
                              onUploadStart={() => setIsUploading(true)}
                              onUploadEnd={() => setIsUploading(false)}
                              onUploadComplete={(url) => setFormData({
                                ...formData,
                                response_payload: { link: url }
                              })}
                              label="Upload Audio"
                              showPreview={false}
                            />
                          ) : (
                            <div>
                              <Label htmlFor="audio_link">URL Audio</Label>
                              <Input
                                id="audio_link"
                                value={formData.response_payload.link}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { link: e.target.value }
                                })}
                                placeholder="https://example.com/audio.mp3"
                                required
                                data-testid="audio-link-input"
                              />
                            </div>
                          )}
                        </>
                      )}

                      {formData.response_type === 'location' && (
                        <>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label htmlFor="latitude">Latitude</Label>
                              <Input
                                id="latitude"
                                value={formData.response_payload.latitude}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, latitude: e.target.value }
                                })}
                                placeholder="-6.200000"
                                required
                                data-testid="latitude-input"
                              />
                            </div>
                            <div>
                              <Label htmlFor="longitude">Longitude</Label>
                              <Input
                                id="longitude"
                                value={formData.response_payload.longitude}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, longitude: e.target.value }
                                })}
                                placeholder="106.816666"
                                required
                                data-testid="longitude-input"
                              />
                            </div>
                          </div>
                          <div>
                            <Label htmlFor="location_name">Nama Lokasi</Label>
                            <Input
                              id="location_name"
                              value={formData.response_payload.name}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, name: e.target.value }
                              })}
                              placeholder="Kantor Kami"
                              required
                              data-testid="location-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="address">Alamat</Label>
                            <Textarea
                              id="address"
                              value={formData.response_payload.address}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, address: e.target.value }
                              })}
                              placeholder="Jl. Sudirman No. 123, Jakarta"
                              rows={2}
                              required
                              data-testid="address-input"
                            />
                          </div>
                        </>
                      )}

                      {formData.response_type === 'contact' && (
                        <>
                          <div>
                            <Label htmlFor="contact_name">Nama Lengkap</Label>
                            <Input
                              id="contact_name"
                              value={formData.response_payload.name}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, name: e.target.value }
                              })}
                              placeholder="Customer Service"
                              required
                              data-testid="contact-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="first_name">Nama Depan</Label>
                            <Input
                              id="first_name"
                              value={formData.response_payload.first_name}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, first_name: e.target.value }
                              })}
                              placeholder="CS"
                              required
                              data-testid="first-name-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="contact_phone">Nomor Telepon (hanya angka)</Label>
                            <Input
                              id="contact_phone"
                              value={formData.response_payload.phone}
                              onChange={(e) => {
                                // Filter only numbers, remove all symbols and spaces
                                const filtered = e.target.value.replace(/[^\d]/g, '');
                                setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, phone: filtered }
                                });
                              }}
                              placeholder="6281234567890"
                              required
                              data-testid="contact-phone-input"
                            />
                            <p className="text-xs text-gray-500 mt-1">Contoh: 6281234567890 (tanpa +, spasi, atau simbol)</p>
                          </div>
                        </>
                      )}

                      {formData.response_type === 'cta_url' && (
                        <>
                          <div>
                            <Label htmlFor="cta_header_image">Header Gambar (Opsional)</Label>
                            <div className="flex items-center gap-2 mb-2">
                              <Button
                                type="button"
                                variant={useUpload.cta_header ? "default" : "outline"}
                                size="sm"
                                onClick={() => setUseUpload({ ...useUpload, cta_header: true })}
                              >
                                <UploadIcon className="w-4 h-4 mr-1" />
                                Upload File
                              </Button>
                              <Button
                                type="button"
                                variant={!useUpload.cta_header ? "default" : "outline"}
                                size="sm"
                                onClick={() => setUseUpload({ ...useUpload, cta_header: false })}
                              >
                                Gunakan URL
                              </Button>
                            </div>
                            {useUpload.cta_header ? (
                              <MediaUploader
                                mediaType="image"
                                accept="image/*"
                                onUploadStart={() => setIsUploading(true)}
                                onUploadEnd={() => setIsUploading(false)}
                                onUploadComplete={(url) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, header_image: url }
                                })}
                                label="Upload Header Image"
                              />
                            ) : (
                              <Input
                                id="cta_header_image"
                                value={formData.response_payload.header_image || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, header_image: e.target.value }
                                })}
                                placeholder="https://example.com/header.jpg"
                                data-testid="cta-header-image-input"
                              />
                            )}
                            <p className="text-xs text-gray-500 mt-1">Tambahkan gambar header untuk tombol URL</p>
                          </div>
                          <div>
                            <Label htmlFor="cta_body">Pesan Teks</Label>
                            <Textarea
                              id="cta_body"
                              value={formData.response_payload.body}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, body: e.target.value }
                              })}
                              placeholder="Kunjungi website kami untuk info lebih lanjut"
                              rows={3}
                              required
                              data-testid="cta-body-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="button_text">Teks Tombol</Label>
                            <Input
                              id="button_text"
                              value={formData.response_payload.button_text}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, button_text: e.target.value }
                              })}
                              placeholder="Buka Website"
                              maxLength={20}
                              required
                              data-testid="button-text-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="cta_url">URL</Label>
                            <Input
                              id="cta_url"
                              value={formData.response_payload.url}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, url: e.target.value }
                              })}
                              placeholder="https://www.example.com"
                              required
                              data-testid="cta-url-input"
                            />
                          </div>
                        </>
                      )}

                      {formData.response_type === 'button' && (
                        <>
                          <div>
                            <Label htmlFor="button_header_image">Header Gambar (Opsional)</Label>
                            <div className="flex items-center gap-2 mb-2">
                              <Button
                                type="button"
                                variant={useUpload.button_header ? "default" : "outline"}
                                size="sm"
                                onClick={() => setUseUpload({ ...useUpload, button_header: true })}
                              >
                                <UploadIcon className="w-4 h-4 mr-1" />
                                Upload File
                              </Button>
                              <Button
                                type="button"
                                variant={!useUpload.button_header ? "default" : "outline"}
                                size="sm"
                                onClick={() => setUseUpload({ ...useUpload, button_header: false })}
                              >
                                Gunakan URL
                              </Button>
                            </div>
                            {useUpload.button_header ? (
                              <MediaUploader
                                mediaType="image"
                                accept="image/*"
                                onUploadStart={() => setIsUploading(true)}
                                onUploadEnd={() => setIsUploading(false)}
                                onUploadComplete={(url) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, header_image: url }
                                })}
                                label="Upload Header Image"
                              />
                            ) : (
                              <Input
                                id="button_header_image"
                                value={formData.response_payload.header_image || ''}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  response_payload: { ...formData.response_payload, header_image: e.target.value }
                                })}
                                placeholder="https://example.com/header.jpg"
                                data-testid="button-header-image-input"
                              />
                            )}
                            <p className="text-xs text-gray-500 mt-1">Tambahkan gambar header untuk tombol balasan</p>
                          </div>
                          <div>
                            <Label htmlFor="button_body">Teks Pesan</Label>
                            <Textarea
                              id="button_body"
                              value={formData.response_payload.body}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, body: e.target.value }
                              })}
                              placeholder="Pilih opsi di bawah ini:"
                              rows={3}
                              required
                              data-testid="button-body-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <Label>Tombol (Maks 3)</Label>
                              <Button
                                type="button"
                                onClick={addButton}
                                size="sm"
                                variant="outline"
                                disabled={formData.response_payload.buttons.length >= 3}
                                data-testid="add-button"
                              >
                                <Plus className="w-4 h-4 mr-1" /> Tambah Tombol
                              </Button>
                            </div>
                            {formData.response_payload.buttons.map((button, index) => (
                              <div key={index} className="border rounded-lg p-3 space-y-2 bg-gray-50">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-medium">Tombol {index + 1}</span>
                                  <Button
                                    type="button"
                                    onClick={() => removeButton(index)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600"
                                    data-testid={`remove-button-${index}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                                <Input
                                  placeholder="ID Tombol (kosongkan untuk auto-generate)"
                                  value={button.id}
                                  onChange={(e) => updateButton(index, 'id', e.target.value)}
                                  data-testid={`button-id-${index}`}
                                />
                                <Input
                                  placeholder="Teks Tombol (maks 20 karakter)"
                                  value={button.title}
                                  onChange={(e) => updateButton(index, 'title', e.target.value)}
                                  maxLength={20}
                                  required
                                  data-testid={`button-title-${index}`}
                                />
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {formData.response_type === 'list' && (
                        <>
                          <div>
                            <Label htmlFor="list_body">Teks Pesan</Label>
                            <Textarea
                              id="list_body"
                              value={formData.response_payload.body}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, body: e.target.value }
                              })}
                              placeholder="Pilih dari menu kami:"
                              rows={3}
                              required
                              data-testid="list-body-input"
                            />
                          </div>
                          <div>
                            <Label htmlFor="buttonText">Teks Tombol Menu</Label>
                            <Input
                              id="buttonText"
                              value={formData.response_payload.buttonText}
                              onChange={(e) => setFormData({
                                ...formData,
                                response_payload: { ...formData.response_payload, buttonText: e.target.value }
                              })}
                              placeholder="Menu"
                              maxLength={20}
                              required
                              data-testid="list-button-text-input"
                            />
                          </div>
                          <div className="space-y-3">
                            <div className="flex justify-between items-center">
                              <Label>Seksi (Total baris: {formData.response_payload.sections.reduce((sum, s) => sum + s.rows.length, 0)}/10)</Label>
                              <Button
                                type="button"
                                onClick={addSection}
                                size="sm"
                                variant="outline"
                                disabled={formData.response_payload.sections.length >= 10}
                                data-testid="add-section"
                              >
                                <Plus className="w-4 h-4 mr-1" /> Tambah Seksi
                              </Button>
                            </div>
                            {formData.response_payload.sections.map((section, sIndex) => (
                              <div key={sIndex} className="border-2 border-blue-200 rounded-lg p-4 space-y-3 bg-blue-50">
                                <div className="flex justify-between items-center">
                                  <span className="text-sm font-semibold">Seksi {sIndex + 1}</span>
                                  <Button
                                    type="button"
                                    onClick={() => removeSection(sIndex)}
                                    size="sm"
                                    variant="ghost"
                                    className="text-red-600"
                                    data-testid={`remove-section-${sIndex}`}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                </div>
                                <Input
                                  placeholder="Judul Seksi (maks 24 karakter)"
                                  value={section.title}
                                  onChange={(e) => updateSection(sIndex, 'title', e.target.value)}
                                  maxLength={24}
                                  required
                                  data-testid={`section-title-${sIndex}`}
                                />
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center">
                                    <Label className="text-sm">Baris</Label>
                                    <Button
                                      type="button"
                                      onClick={() => addRow(sIndex)}
                                      size="sm"
                                      variant="outline"
                                      disabled={formData.response_payload.sections.reduce((sum, s) => sum + s.rows.length, 0) >= 10}
                                      data-testid={`add-row-${sIndex}`}
                                    >
                                      <Plus className="w-3 h-3 mr-1" /> Tambah Baris
                                    </Button>
                                  </div>
                                  {section.rows.map((row, rIndex) => (
                                    <div key={rIndex} className="border rounded-lg p-3 space-y-2 bg-white">
                                      <div className="flex justify-between items-center">
                                        <span className="text-xs font-medium">Baris {rIndex + 1}</span>
                                        <Button
                                          type="button"
                                          onClick={() => removeRow(sIndex, rIndex)}
                                          size="sm"
                                          variant="ghost"
                                          className="text-red-600"
                                          data-testid={`remove-row-${sIndex}-${rIndex}`}
                                        >
                                          <X className="w-3 h-3" />
                                        </Button>
                                      </div>
                                      <Input
                                        placeholder="ID Baris (kosongkan untuk auto-generate)"
                                        value={row.id}
                                        onChange={(e) => updateRow(sIndex, rIndex, 'id', e.target.value)}
                                        data-testid={`row-id-${sIndex}-${rIndex}`}
                                      />
                                      <Input
                                        placeholder="Judul Baris (maks 24 karakter)"
                                        value={row.title}
                                        onChange={(e) => updateRow(sIndex, rIndex, 'title', e.target.value)}
                                        maxLength={24}
                                        required
                                        data-testid={`row-title-${sIndex}-${rIndex}`}
                                      />
                                      <Textarea
                                        placeholder="Deskripsi (maks 72 karakter)"
                                        value={row.description}
                                        onChange={(e) => updateRow(sIndex, rIndex, 'description', e.target.value)}
                                        maxLength={72}
                                        rows={2}
                                        data-testid={`row-description-${sIndex}-${rIndex}`}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                      </>
                      )}

                      {/* Multiple Responses UI */}
                      {multiResponseMode && (
                        <div className="space-y-4">
                          {responses.map((response, index) => (
                            <Card key={index} className="border border-blue-200 bg-blue-50/30">
                              <CardHeader className="pb-3">
                                <div className="flex items-center justify-between">
                                  <CardTitle className="text-sm font-semibold">
                                    Respons {index + 1}
                                  </CardTitle>
                                  <div className="flex gap-1">
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => moveResponse(index, 'up')}
                                      disabled={index === 0}
                                      className="h-7 w-7 p-0"
                                    >
                                      <ArrowUp className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => moveResponse(index, 'down')}
                                      disabled={index === responses.length - 1}
                                      className="h-7 w-7 p-0"
                                    >
                                      <ArrowDown className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => removeResponse(index)}
                                      disabled={responses.length === 1}
                                      className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </div>
                              </CardHeader>
                              <CardContent className="space-y-3">
                                <div>
                                  <Label>Tipe Respons</Label>
                                  <Select
                                    value={response.response_type}
                                    onValueChange={(value) => updateResponse(index, 'response_type', value)}
                                  >
                                    <SelectTrigger>
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="text">Teks</SelectItem>
                                      <SelectItem value="image">Gambar</SelectItem>
                                      <SelectItem value="video">Video</SelectItem>
                                      <SelectItem value="audio">Audio</SelectItem>
                                      <SelectItem value="document">Dokumen</SelectItem>
                                      <SelectItem value="location">Lokasi</SelectItem>
                                      <SelectItem value="contact">Kontak</SelectItem>
                                      <SelectItem value="button">Tombol Balasan</SelectItem>
                                      <SelectItem value="cta_url">Tombol URL</SelectItem>
                                    </SelectContent>
                                  </Select>
                                </div>

                                {/* TEXT */}
                                {response.response_type === 'text' && (
                                  <div>
                                    <Label>Teks Pesan</Label>
                                    <Textarea
                                      placeholder="Masukkan teks pesan..."
                                      value={response.response_payload.body || ''}
                                      onChange={(e) => updateResponse(index, 'payload', { body: e.target.value })}
                                      rows={3}
                                    />
                                  </div>
                                )}

                                {/* IMAGE */}
                                {response.response_type === 'image' && (
                                  <>
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <Label>Gambar</Label>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant={useUpload[`image_${index}`] ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setUseUpload({ ...useUpload, [`image_${index}`]: true })}
                                            className="h-8 text-xs"
                                          >
                                            <UploadIcon className="w-3 h-3 mr-1" />
                                            Upload
                                          </Button>
                                          <Button
                                            type="button"
                                            variant={!useUpload[`image_${index}`] ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setUseUpload({ ...useUpload, [`image_${index}`]: false })}
                                            className="h-8 text-xs"
                                          >
                                            URL
                                          </Button>
                                        </div>
                                      </div>
                                      {useUpload[`image_${index}`] ? (
                                        <MediaUploader
                                          mediaType="image"
                                          accept="image/*"
                                          onUploadStart={() => setIsUploading(true)}
                                          onUploadEnd={() => setIsUploading(false)}
                                          onUploadComplete={(url) => updateResponse(index, 'payload', { ...response.response_payload, link: url })}
                                          label=""
                                        />
                                      ) : (
                                        <Input
                                          placeholder="https://example.com/image.jpg"
                                          value={response.response_payload.link || ''}
                                          onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, link: e.target.value })}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <Label>Caption (Opsional)</Label>
                                      <Textarea
                                        placeholder="Tambahkan caption..."
                                        value={response.response_payload.caption || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, caption: e.target.value })}
                                        rows={2}
                                      />
                                    </div>
                                  </>
                                )}

                                {/* VIDEO */}
                                {response.response_type === 'video' && (
                                  <>
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <Label>Video</Label>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant={useUpload[`video_${index}`] ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setUseUpload({ ...useUpload, [`video_${index}`]: true })}
                                            className="h-8 text-xs"
                                          >
                                            <UploadIcon className="w-3 h-3 mr-1" />
                                            Upload
                                          </Button>
                                          <Button
                                            type="button"
                                            variant={!useUpload[`video_${index}`] ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setUseUpload({ ...useUpload, [`video_${index}`]: false })}
                                            className="h-8 text-xs"
                                          >
                                            URL
                                          </Button>
                                        </div>
                                      </div>
                                      {useUpload[`video_${index}`] ? (
                                        <MediaUploader
                                          mediaType="video"
                                          accept="video/mp4,video/3gpp"
                                          onUploadStart={() => setIsUploading(true)}
                                          onUploadEnd={() => setIsUploading(false)}
                                          onUploadComplete={(url) => updateResponse(index, 'payload', { ...response.response_payload, link: url })}
                                          label=""
                                          showPreview={false}
                                        />
                                      ) : (
                                        <Input
                                          placeholder="https://example.com/video.mp4"
                                          value={response.response_payload.link || ''}
                                          onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, link: e.target.value })}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <Label>Caption (Opsional)</Label>
                                      <Textarea
                                        placeholder="Tambahkan caption..."
                                        value={response.response_payload.caption || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, caption: e.target.value })}
                                        rows={2}
                                      />
                                    </div>
                                  </>
                                )}

                                {/* AUDIO */}
                                {response.response_type === 'audio' && (
                                  <div>
                                    <div className="flex items-center justify-between mb-2">
                                      <Label>Audio</Label>
                                      <div className="flex gap-2">
                                        <Button
                                          type="button"
                                          variant={useUpload[`audio_${index}`] ? "default" : "outline"}
                                          size="sm"
                                          onClick={() => setUseUpload({ ...useUpload, [`audio_${index}`]: true })}
                                          className="h-8 text-xs"
                                        >
                                          <UploadIcon className="w-3 h-3 mr-1" />
                                          Upload
                                        </Button>
                                        <Button
                                          type="button"
                                          variant={!useUpload[`audio_${index}`] ? "default" : "outline"}
                                          size="sm"
                                          onClick={() => setUseUpload({ ...useUpload, [`audio_${index}`]: false })}
                                          className="h-8 text-xs"
                                        >
                                          URL
                                        </Button>
                                      </div>
                                    </div>
                                    {useUpload[`audio_${index}`] ? (
                                      <MediaUploader
                                        mediaType="audio"
                                        accept="audio/*"
                                        onUploadStart={() => setIsUploading(true)}
                                        onUploadEnd={() => setIsUploading(false)}
                                        onUploadComplete={(url) => updateResponse(index, 'payload', { link: url })}
                                        label=""
                                        showPreview={false}
                                      />
                                    ) : (
                                      <Input
                                        placeholder="https://example.com/audio.mp3"
                                        value={response.response_payload.link || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { link: e.target.value })}
                                      />
                                    )}
                                  </div>
                                )}

                                {/* DOCUMENT */}
                                {response.response_type === 'document' && (
                                  <>
                                    <div>
                                      <div className="flex items-center justify-between mb-2">
                                        <Label>Dokumen</Label>
                                        <div className="flex gap-2">
                                          <Button
                                            type="button"
                                            variant={useUpload[`document_${index}`] ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setUseUpload({ ...useUpload, [`document_${index}`]: true })}
                                            className="h-8 text-xs"
                                          >
                                            <UploadIcon className="w-3 h-3 mr-1" />
                                            Upload
                                          </Button>
                                          <Button
                                            type="button"
                                            variant={!useUpload[`document_${index}`] ? "default" : "outline"}
                                            size="sm"
                                            onClick={() => setUseUpload({ ...useUpload, [`document_${index}`]: false })}
                                            className="h-8 text-xs"
                                          >
                                            URL
                                          </Button>
                                        </div>
                                      </div>
                                      {useUpload[`document_${index}`] ? (
                                        <MediaUploader
                                          mediaType="document"
                                          accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                                          onUploadStart={() => setIsUploading(true)}
                                          onUploadEnd={() => setIsUploading(false)}
                                          onUploadComplete={(url) => updateResponse(index, 'payload', { ...response.response_payload, link: url })}
                                          label=""
                                          showPreview={false}
                                        />
                                      ) : (
                                        <Input
                                          placeholder="https://example.com/document.pdf"
                                          value={response.response_payload.link || ''}
                                          onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, link: e.target.value })}
                                        />
                                      )}
                                    </div>
                                    <div>
                                      <Label>Nama File (Opsional)</Label>
                                      <Input
                                        placeholder="dokumen.pdf"
                                        value={response.response_payload.filename || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, filename: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <Label>Caption (Opsional)</Label>
                                      <Textarea
                                        placeholder="Tambahkan caption..."
                                        value={response.response_payload.caption || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, caption: e.target.value })}
                                        rows={2}
                                      />
                                    </div>
                                  </>
                                )}

                                {/* LOCATION */}
                                {response.response_type === 'location' && (
                                  <>
                                    <div>
                                      <Label>Nama Lokasi</Label>
                                      <Input
                                        placeholder="Kantor Pusat"
                                        value={response.response_payload.name || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, name: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <Label>Alamat</Label>
                                      <Input
                                        placeholder="Jl. Sudirman No. 123"
                                        value={response.response_payload.address || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, address: e.target.value })}
                                      />
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label>Latitude</Label>
                                        <Input
                                          type="number"
                                          step="any"
                                          placeholder="-6.200000"
                                          value={response.response_payload.latitude || ''}
                                          onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, latitude: e.target.value })}
                                        />
                                      </div>
                                      <div>
                                        <Label>Longitude</Label>
                                        <Input
                                          type="number"
                                          step="any"
                                          placeholder="106.816666"
                                          value={response.response_payload.longitude || ''}
                                          onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, longitude: e.target.value })}
                                        />
                                      </div>
                                    </div>
                                  </>
                                )}

                                {/* CONTACT */}
                                {response.response_type === 'contact' && (
                                  <>
                                    <div>
                                      <Label>Nama Lengkap</Label>
                                      <Input
                                        placeholder="John Doe"
                                        value={response.response_payload.name || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, name: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <Label>Nomor Telepon</Label>
                                      <Input
                                        placeholder="628123456789"
                                        value={response.response_payload.phone || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, phone: e.target.value })}
                                      />
                                    </div>
                                  </>
                                )}

                                {/* BUTTON */}
                                {response.response_type === 'button' && (
                                  <>
                                    <div>
                                      <Label>Header Gambar (Opsional)</Label>
                                      <Input
                                        placeholder="https://example.com/header.jpg"
                                        value={response.response_payload.header_image || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, header_image: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <Label>Teks Pesan</Label>
                                      <Textarea
                                        placeholder="Pilih opsi di bawah ini:"
                                        value={response.response_payload.body || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, body: e.target.value })}
                                        rows={2}
                                      />
                                    </div>
                                    <div>
                                      <Label>Tombol (Maks 3)</Label>
                                      {(response.response_payload.buttons || []).map((btn, btnIdx) => (
                                        <div key={btnIdx} className="flex gap-2 mb-2">
                                          <Input
                                            placeholder="ID"
                                            value={btn.id || ''}
                                            onChange={(e) => {
                                              const newButtons = [...(response.response_payload.buttons || [])];
                                              newButtons[btnIdx] = { ...newButtons[btnIdx], id: e.target.value };
                                              updateResponse(index, 'payload', { ...response.response_payload, buttons: newButtons });
                                            }}
                                            className="w-1/3"
                                          />
                                          <Input
                                            placeholder="Teks Tombol"
                                            value={btn.title || ''}
                                            onChange={(e) => {
                                              const newButtons = [...(response.response_payload.buttons || [])];
                                              newButtons[btnIdx] = { ...newButtons[btnIdx], title: e.target.value };
                                              updateResponse(index, 'payload', { ...response.response_payload, buttons: newButtons });
                                            }}
                                            className="flex-1"
                                          />
                                          <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              const newButtons = (response.response_payload.buttons || []).filter((_, i) => i !== btnIdx);
                                              updateResponse(index, 'payload', { ...response.response_payload, buttons: newButtons });
                                            }}
                                            className="text-red-600"
                                          >
                                            <Trash2 className="w-4 h-4" />
                                          </Button>
                                        </div>
                                      ))}
                                      {(!response.response_payload.buttons || response.response_payload.buttons.length < 3) && (
                                        <Button
                                          type="button"
                                          variant="outline"
                                          size="sm"
                                          onClick={() => {
                                            const newButtons = [...(response.response_payload.buttons || []), { id: '', title: '' }];
                                            updateResponse(index, 'payload', { ...response.response_payload, buttons: newButtons });
                                          }}
                                          className="w-full"
                                        >
                                          <Plus className="w-4 h-4 mr-2" />
                                          Tambah Tombol
                                        </Button>
                                      )}
                                    </div>
                                  </>
                                )}

                                {/* CTA URL */}
                                {response.response_type === 'cta_url' && (
                                  <>
                                    <div>
                                      <Label>Header Gambar (Opsional)</Label>
                                      <Input
                                        placeholder="https://example.com/header.jpg"
                                        value={response.response_payload.header_image || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, header_image: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <Label>Pesan Teks</Label>
                                      <Textarea
                                        placeholder="Kunjungi website kami"
                                        value={response.response_payload.body || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, body: e.target.value })}
                                        rows={2}
                                      />
                                    </div>
                                    <div>
                                      <Label>URL Tujuan</Label>
                                      <Input
                                        placeholder="https://example.com"
                                        value={response.response_payload.url || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, url: e.target.value })}
                                      />
                                    </div>
                                    <div>
                                      <Label>Teks Tombol</Label>
                                      <Input
                                        placeholder="Kunjungi Website"
                                        value={response.response_payload.button_text || ''}
                                        onChange={(e) => updateResponse(index, 'payload', { ...response.response_payload, button_text: e.target.value })}
                                        maxLength={20}
                                      />
                                    </div>
                                  </>
                                )}

                              </CardContent>
                            </Card>
                          ))}
                          
                          {responses.length < 5 && (
                            <Button
                              type="button"
                              onClick={addResponse}
                              variant="outline"
                              className="w-full border-dashed border-2 hover:border-blue-500 hover:bg-blue-50"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Tambah Respons ({responses.length}/5)
                            </Button>
                          )}
                        </div>
                      )}

                      <div className="flex gap-2">
                        <Button
                          type="submit"
                          disabled={loading || isUploading}
                          className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-all duration-300"
                          data-testid="submit-rule-button"
                        >
                          {isUploading ? 'Mengupload...' : loading ? 'Menyimpan...' : editingRule ? 'Perbarui Aturan' : 'Buat Aturan'}
                        </Button>
                        {editingRule && (
                          <Button
                            type="button"
                            onClick={resetForm}
                            variant="outline"
                            data-testid="cancel-edit-button"
                          >
                            Batal
                          </Button>
                        )}
                      </div>
                    </form>
                  </CardContent>
                </Card>
                )}

                {/* Rules List */}
                <Card className="border-2 border-blue-200 shadow-lg">
                  <CardHeader>
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                      <div>
                        <CardTitle>Aturan Chatbot ({rules.length})</CardTitle>
                        <CardDescription>Semua aturan untuk akun yang dipilih</CardDescription>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* Import Button */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => document.getElementById('import-file-input').click()}
                                data-testid="import-rules-button"
                              >
                                <UploadIcon className="w-4 h-4 mr-2" />
                                Import
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Import rules dari file JSON</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <input
                          id="import-file-input"
                          type="file"
                          accept=".json"
                          onChange={handleImportRules}
                          className="hidden"
                        />
                        
                        {/* Export Button */}
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={handleExportRules}
                                disabled={rules.length === 0}
                                data-testid="export-rules-button"
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Export
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>Export rules ke file JSON</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        {!showRuleForm && (
                          <Button
                            onClick={() => setShowRuleForm(true)}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                            data-testid="create-rule-button"
                          >
                            <Plus className="w-4 h-4 mr-2" />
                            Buat Aturan
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Search & Filter */}
                    <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="md:col-span-2">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input
                            placeholder="Cari keyword..."
                            value={searchKeyword}
                            onChange={(e) => handleSearch(e.target.value)}
                            className="pl-10"
                            data-testid="search-input"
                          />
                        </div>
                      </div>
                      <div>
                        <Select value={filterType || "all"} onValueChange={(value) => handleFilterType(value === "all" ? "" : value)}>
                          <SelectTrigger data-testid="filter-type-select">
                            <SelectValue placeholder="Semua Tipe" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Semua Tipe</SelectItem>
                            <SelectItem value="text">Teks</SelectItem>
                            <SelectItem value="image">Gambar</SelectItem>
                            <SelectItem value="video">Video</SelectItem>
                            <SelectItem value="audio">Audio</SelectItem>
                            <SelectItem value="document">Dokumen</SelectItem>
                            <SelectItem value="location">Lokasi</SelectItem>
                            <SelectItem value="contact">Kontak</SelectItem>
                            <SelectItem value="button">Tombol Balasan</SelectItem>
                            <SelectItem value="cta_url">Tombol URL</SelectItem>
                            <SelectItem value="list">Daftar Interaktif</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    {rules.length === 0 ? (
                      <p className="text-center text-gray-500 py-8" data-testid="no-rules-message">Belum ada aturan dibuat</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Keyword</TableHead>
                              <TableHead>Tipe Pencocokan</TableHead>
                              <TableHead>Tipe Respons</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead className="text-right">Aksi</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody data-testid="rules-table">
                            {rules.map((rule, index) => (
                              <TableRow 
                                key={rule.id} 
                                className="table-row-hover"
                                style={{ animationDelay: `${index * 0.05}s` }}
                              >
                                <TableCell className="font-medium">
                                  {rule.keywords && rule.keywords.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {rule.keywords.map((kw, idx) => (
                                        <span 
                                          key={idx}
                                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                                        >
                                          {kw}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span>{rule.keyword}</span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    {rule.match_type}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {rule.responses && rule.responses.length > 0 ? (
                                    <div className="flex flex-wrap gap-1">
                                      {rule.responses.map((resp, idx) => (
                                        <span 
                                          key={idx} 
                                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                          {idx + 1}. {resp.response_type}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      {rule.response_type}
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={rule.is_active !== false}
                                      onChange={() => handleToggleActive(rule.id, rule.is_active !== false)}
                                      className="sr-only peer"
                                      data-testid={`toggle-active-${rule.id}`}
                                    />
                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                                    <span className="ml-3 text-sm font-medium text-gray-700">
                                      {rule.is_active !== false ? 'Aktif' : 'Nonaktif'}
                                    </span>
                                  </label>
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex gap-1 justify-end">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(rule)}
                                      className="text-blue-600 hover:text-blue-700 hover:scale-110 transition-transform duration-200"
                                      data-testid={`edit-rule-${rule.id}`}
                                    >
                                      <Edit className="w-4 h-4" />
                                    </Button>
                                    <AlertDialog>
                                      <AlertDialogTrigger asChild>
                                        <Button variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:scale-110 transition-transform duration-200" data-testid={`delete-rule-${rule.id}`}>
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </AlertDialogTrigger>
                                      <AlertDialogContent>
                                        <AlertDialogHeader>
                                          <AlertDialogTitle>Delete Rule?</AlertDialogTitle>
                                          <AlertDialogDescription>
                                            This will permanently delete this chatbot rule. This action cannot be undone.
                                          </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                                          <AlertDialogAction onClick={() => handleDelete(rule.id)} className="bg-red-600 hover:bg-red-700">
                                            Delete
                                          </AlertDialogAction>
                                        </AlertDialogFooter>
                                      </AlertDialogContent>
                                    </AlertDialog>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}

                    {/* Pagination */}
                    {rules.length > 0 && totalPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t">
                        <div className="text-sm text-gray-600">
                          Menampilkan {rules.length} dari {totalRules} aturan (Halaman {currentPage} dari {totalPages})
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            data-testid="prev-page-btn"
                          >
                            <ChevronLeft className="w-4 h-4 mr-1" />
                            Sebelumnya
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            data-testid="next-page-btn"
                          >
                            Selanjutnya
                            <ChevronRight className="w-4 h-4 ml-1" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
